import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import StatsCard from '../../components/common/StatsCard';
import {
  Download,
  Calendar,
  TrendingUp,
  DollarSign,
  Users,
  Activity,
} from 'lucide-react';
import SourceCombobox from '../../components/common/SourceCombobox';
import {
  reportTypeOptions,
  dateRangeOptions,
  exportFormatOptions,
} from '../../constants/adminReportsData';

const getOptionLabel = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string
) => options.find(option => option.value === value)?.label || '';

const Reports = () => {
  const [reportType, setReportType] = useState('client');
  const [dateRange, setDateRange] = useState('today');
  const [exportFormat, setExportFormat] = useState('pdf');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Reports</h1>
        <p className="text-muted-foreground">
          Generate and download various reports
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Monthly Revenue"
          value="$125,000"
          change="+15% from last month"
          icon={DollarSign}
        />

        <StatsCard
          title="Active Clients"
          value="756"
          change="+3% from last month"
          icon={Users}
        />

        <StatsCard
          title="Total Trades"
          value="1,204"
          change="+10% from last month"
          icon={Activity}
        />

        <StatsCard
          title="Success Rate"
          value="68.5%"
          change="+5% from last month"
          icon={TrendingUp}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Generate Report</CardTitle>
            <CardDescription>
              Create custom reports with specific parameters
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reportType">Report Type</Label>
              <SourceCombobox
                id="reportType"
                items={reportTypeOptions.map(option => option.label)}
                placeholder="Select report type"
                value={getOptionLabel(reportTypeOptions, reportType)}
                onChange={value => {
                  const option = reportTypeOptions.find(
                    item => item.label === value
                  );
                  if (option) setReportType(option.value);
                }}
                emptyText="No report types found."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dateRange">Date Range</Label>
              <SourceCombobox
                id="dateRange"
                items={dateRangeOptions.map(option => option.label)}
                placeholder="Select date range"
                value={getOptionLabel(dateRangeOptions, dateRange)}
                onChange={value => {
                  const option = dateRangeOptions.find(
                    item => item.label === value
                  );
                  if (option) setDateRange(option.value);
                }}
                emptyText="No date ranges found."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input id="startDate" type="date" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input id="endDate" type="date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="format">Export Format</Label>
              <SourceCombobox
                id="format"
                items={exportFormatOptions.map(option => option.label)}
                placeholder="Select export format"
                value={getOptionLabel(exportFormatOptions, exportFormat)}
                onChange={value => {
                  const option = exportFormatOptions.find(
                    item => item.label === value
                  );
                  if (option) setExportFormat(option.value);
                }}
                emptyText="No export formats found."
              />
            </div>

            <Button className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Reports</CardTitle>
            <CardDescription>Download pre-configured reports</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <Button variant="outline" className="w-full justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Daily Trading Summary
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Users className="h-4 w-4 mr-2" />
                Client Activity Report
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <DollarSign className="h-4 w-4 mr-2" />
                Financial Statement
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Activity className="h-4 w-4 mr-2" />
                Performance Metrics
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <TrendingUp className="h-4 w-4 mr-2" />
                Market Analysis
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scheduled Reports</CardTitle>
          <CardDescription>
            Manage your automated report schedules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Daily Trading Report</p>
                <p className="text-sm text-muted-foreground">
                  Sent at 9:00 AM daily
                </p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  Delete
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Weekly Financial Summary</p>
                <p className="text-sm text-muted-foreground">
                  Sent every Monday at 8:00 AM
                </p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  Delete
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Monthly Performance Report</p>
                <p className="text-sm text-muted-foreground">
                  Sent on 1st of each month
                </p>
              </div>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
